import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Modal from "../../components/Modal";
import EscapeHandler from "../../utils/escapeHandler";
import {
  fetchPrevisionProjects,
  savePrevisionMapping,
  syncPrevisionBaseline,
} from "./integrationsApi";

const STATUS_LABELS = Object.freeze({
  success: "Base sincronizada",
  unchanged: "Base sincronizada",
  pending_apply: "Sincronizando…",
  running: "Sincronizando…",
  fallback_applied: "Usando última Base válida",
  no_active_baseline: "Projeto sem baseline ativa",
  error_transient: "Falha temporária",
  error_permanent: "Revisar configuração",
  partial_failure: "Falha de aplicação — acionar suporte",
});

const SUCCESS_STATUSES = new Set(["success", "unchanged"]);
const RUNNING_STATUSES = new Set(["pending_apply", "running"]);
const SCHEDULE_STATUS_LABELS = Object.freeze({
  success: "Cronograma sincronizado",
  unchanged: "Cronograma sincronizado",
  pending_apply: "Aplicando cronograma…",
  running: "Sincronizando cronograma…",
  fallback_applied: "Cronograma em fallback",
  error_transient: "Falha temporária no cronograma",
  error_permanent: "Revisar configuração do cronograma",
  partial_failure: "Falha parcial no cronograma",
});
const SCHEDULE_ERROR_PRESENTATION = Object.freeze({
  schedule_budgeted_unit_uncovered: {
    label: "Cronograma sem cobertura completa",
    message: "Há unidades orçadas sem correspondência canônica no Prevision. "
      + "Corrija as células no Prevision ou acione o suporte para definir uma regra de agregação; "
      + "nenhuma curva foi replicada automaticamente.",
  },
  schedule_capture_failed: {
    label: "Falha ao processar o cronograma",
    message: "O cronograma Prevision não pôde ser processado. "
      + "Tente sincronizar novamente e, se a falha persistir, acione o suporte.",
  },
});

const refreshKeys = ({ buildingId, projectIds = [] }) => [
  `building:${buildingId}`,
  ...[...new Set(projectIds
    .filter((value) => typeof value === "string" && value.length > 0))]
    .map((projectId) => `project:${projectId}`),
];

export const createRefreshVersionTracker = () => {
  let sequence = 0;
  const currentByKey = new Map();
  const acceptedByKey = new Map();
  return {
    begin(scope) {
      const ticket = { revision: sequence + 1, keys: refreshKeys(scope) };
      sequence = ticket.revision;
      ticket.keys.forEach((key) => currentByKey.set(key, ticket.revision));
      return ticket;
    },
    isCurrent(ticket) {
      return ticket.keys.every((key) => currentByKey.get(key) === ticket.revision);
    },
    canApply(ticket, scope) {
      return refreshKeys(scope).every((key) => (
        (currentByKey.get(key) ?? 0) <= ticket.revision
      ));
    },
    accept(ticket, scopes) {
      scopes.flatMap(refreshKeys).forEach((key) => {
        if ((currentByKey.get(key) ?? 0) <= ticket.revision) {
          currentByKey.set(key, ticket.revision);
          acceptedByKey.set(key, ticket.revision);
        }
      });
    },
    coveredByNewer(ticket) {
      return ticket.keys.every((key) => (
        (acceptedByKey.get(key) ?? 0) > ticket.revision
      ));
    },
    invalidate() {
      sequence += 1;
      currentByKey.clear();
      acceptedByKey.clear();
    },
  };
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString("pt-BR");
};

const abbreviateId = (value) => {
  if (!value || value.length <= 13) return value;
  return `${value.slice(0, 8)}…${value.slice(-4)}`;
};

const safeError = (error, fallback = "Não foi possível concluir a operação.") => ({
  message: typeof error?.message === "string" ? error.message : fallback,
  correlationId: typeof error?.correlationId === "string" ? error.correlationId : null,
  code: typeof error?.code === "string" ? error.code : null,
});

const buildingIsEligible = (building) => building?.eligibility?.status === "eligible";

const buildingEligibilityMessage = (building) => (
  typeof building?.eligibility?.message === "string" && building.eligibility.message.trim()
    ? building.eligibility.message
    : "A obra ainda não possui uma identidade WBS válida para o Prevision."
);

const ErrorMessage = ({ error }) => error ? (
  <div className="prevision-error" role="alert">
    <p>{error.message}</p>
    {error.correlationId ? <small>Correlation ID: {error.correlationId}</small> : null}
  </div>
) : null;

ErrorMessage.propTypes = {
  error: PropTypes.shape({
    message: PropTypes.string.isRequired,
    correlationId: PropTypes.string,
  }),
};

ErrorMessage.defaultProps = {
  error: null,
};

const StatusTrail = ({ sync }) => {
  if (!sync?.status) return <p className="prevision-status prevision-status--idle">Base ainda não sincronizada</p>;
  const label = STATUS_LABELS[sync.status] ?? "Estado de sincronização indisponível";
  const modifier = SUCCESS_STATUSES.has(sync.status)
    ? "success"
    : RUNNING_STATUSES.has(sync.status)
      ? "running"
      : sync.status === "fallback_applied" || sync.status === "no_active_baseline"
        ? "warning"
        : "error";
  const attempt = formatDate(sync.lastAttemptAt);
  const success = formatDate(sync.lastSuccessAt);

  return (
    <div className={`prevision-status prevision-status--${modifier}`}>
      <b>{label}</b>
      <div className="prevision-status__metadata">
        {attempt ? <span>Última tentativa: {attempt}</span> : null}
        {success ? <span>Último sucesso: {success}</span> : null}
        {sync.activeBaselineId ? (
          <span>
            Base: <code title={sync.activeBaselineId}>{abbreviateId(sync.activeBaselineId)}</code>
          </span>
        ) : null}
        {sync.activeVersionId ? (
          <span>
            Versão: <code title={sync.activeVersionId}>{abbreviateId(sync.activeVersionId)}</code>
          </span>
        ) : null}
      </div>
      <ErrorMessage error={sync.error ? safeError(sync.error) : null} />
    </div>
  );
};

StatusTrail.propTypes = {
  sync: PropTypes.shape({
    status: PropTypes.string,
    lastAttemptAt: PropTypes.string,
    lastSuccessAt: PropTypes.string,
    activeBaselineId: PropTypes.string,
    activeVersionId: PropTypes.string,
    error: PropTypes.shape({
      message: PropTypes.string,
      correlationId: PropTypes.string,
    }),
  }),
};

StatusTrail.defaultProps = {
  sync: null,
};

const ScheduleStatusTrail = ({ sync, loading }) => {
  if (loading) return (
    <div
      className="prevision-status prevision-status--running"
      role="status"
      aria-label="Estado do cronograma Prevision"
    >
      <b>Atualizando estado do cronograma…</b>
    </div>
  );
  if (!sync) return null;
  const presentation = SCHEDULE_ERROR_PRESENTATION[sync.errorCode] ?? null;
  const label = presentation?.label
    ?? SCHEDULE_STATUS_LABELS[sync.status]
    ?? "Estado do cronograma indisponível";
  const modifier = SUCCESS_STATUSES.has(sync.status)
    ? "success"
    : RUNNING_STATUSES.has(sync.status)
      ? "running"
      : sync.usingFallback
        ? "warning"
        : "error";
  const attempt = formatDate(sync.lastAttemptAt);
  const lastValid = formatDate(sync.lastValidAt);
  const usingFallback = sync.usingFallback === true && Boolean(lastValid);

  return (
    <div
      className={`prevision-status prevision-status--${modifier}`}
      role="status"
      aria-label="Estado do cronograma Prevision"
    >
      <b>{label}</b>
      {presentation ? <p>{presentation.message}</p> : null}
      <div className="prevision-status__metadata">
        {attempt ? <span>Última tentativa do cronograma: {attempt}</span> : null}
        {usingFallback ? <span>Usando último cronograma válido de {lastValid}</span> : null}
        {!usingFallback && sync.status === "partial_failure" ? (
          <span>Nenhum cronograma válido foi publicado.</span>
        ) : null}
      </div>
    </div>
  );
};

ScheduleStatusTrail.propTypes = {
  sync: PropTypes.shape({
    status: PropTypes.string,
    errorCode: PropTypes.string,
    lastAttemptAt: PropTypes.string,
    lastValidAt: PropTypes.string,
    usingFallback: PropTypes.bool,
  }),
  loading: PropTypes.bool,
};

ScheduleStatusTrail.defaultProps = {
  sync: null,
  loading: false,
};

const PrevisionProjectsModal = ({ companyId, companyName, onClose }) => {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState({});
  const [requests, setRequests] = useState({});
  const [feedback, setFeedback] = useState({});
  const [projectReservations, setProjectReservations] = useState({});
  const projectReservationsRef = useRef({});
  const mountedRef = useRef(true);
  const refreshVersionTrackerRef = useRef(null);
  if (!refreshVersionTrackerRef.current) {
    refreshVersionTrackerRef.current = createRefreshVersionTracker();
  }
  const financialScheduleEnabled = data?.credential?.capabilities?.financialSchedule !== false;

  useEffect(() => {
    let active = true;
    mountedRef.current = true;
    fetchPrevisionProjects(companyId)
      .then((result) => {
        if (!active) return;
        setData(result);
        setSelectedProjects(Object.fromEntries(
          result.buildings.map((item) => [item.buildingId, item.mapping?.projectId ?? ""])
        ));
      })
      .catch((error) => {
        if (active) setLoadError(safeError(error));
      });
    return () => {
      active = false;
      mountedRef.current = false;
      refreshVersionTrackerRef.current.invalidate();
    };
  }, [companyId]);

  const updateBuilding = (buildingId, update) => {
    if (!mountedRef.current) return;
    setData((current) => current ? ({
      ...current,
      buildings: current.buildings.map((item) => (
        item.buildingId === buildingId ? { ...item, ...update } : item
      )),
    }) : current);
  };

  const applyUnconfirmedMapping = (building, nextMapping) => {
    if (!mountedRef.current) return;
    setData((current) => current ? ({
      ...current,
      buildings: current.buildings.map((item) => (
        item.buildingId === building.buildingId
          ? {
            ...item,
            mapping: nextMapping,
            sync: null,
            scheduleSync: null,
          }
          : item
      )),
      projects: current.projects.map((item) => {
        if (item.id === nextMapping.projectId) {
          return {
            ...item,
            mappedBuildingId: building.buildingId,
            disabled: true,
            disabledReason: `Projeto vinculado à obra ${building.buildingName}.`,
          };
        }
        if (item.mappedBuildingId === building.buildingId) {
          return {
            ...item,
            mappedBuildingId: null,
            disabled: false,
            disabledReason: null,
          };
        }
        return item;
      }),
    }) : current);
  };

  const setRequest = (buildingId, request) => {
    if (!mountedRef.current) return;
    setRequests((current) => ({ ...current, [buildingId]: request }));
  };

  const setBuildingFeedback = (buildingId, nextFeedback) => {
    if (!mountedRef.current) return;
    setFeedback((current) => ({ ...current, [buildingId]: nextFeedback }));
  };

  const applyAuthoritativeData = (result, applicableScopes, buildingId) => {
    const applicableBuildingIds = new Set(
      applicableScopes.map((scope) => scope.buildingId)
    );
    const resultBuildingById = new Map(
      result.buildings.map((item) => [item.buildingId, item])
    );
    const resultProjectById = new Map(result.projects.map((item) => [item.id, item]));
    setData((current) => {
      if (!current) return current;
      const applicableProjectIds = new Set(applicableScopes.flatMap((scope) => scope.projectIds));
      for (const item of [...current.projects, ...result.projects]) {
        if (applicableBuildingIds.has(item.mappedBuildingId)) applicableProjectIds.add(item.id);
      }
      const buildings = current.buildings.map((item) => (
        applicableBuildingIds.has(item.buildingId)
          ? resultBuildingById.get(item.buildingId) ?? {
            ...item,
            mapping: null,
            sync: null,
            scheduleSync: null,
          }
          : item
      ));
      for (const item of result.buildings) {
        if (
          applicableBuildingIds.has(item.buildingId)
          && !buildings.some((candidate) => candidate.buildingId === item.buildingId)
        ) buildings.push(item);
      }
      const projects = current.projects.map((item) => (
        applicableProjectIds.has(item.id)
          ? resultProjectById.get(item.id) ?? {
            ...item,
            mappedBuildingId: null,
            disabled: false,
            disabledReason: null,
          }
          : item
      ));
      for (const item of result.projects) {
        if (
          applicableProjectIds.has(item.id)
          && !projects.some((candidate) => candidate.id === item.id)
        ) projects.push(item);
      }
      return {
        ...current,
        token: result.token,
        credential: result.credential,
        pagination: result.pagination,
        buildings,
        projects,
      };
    });
    setSelectedProjects((current) => {
      const projectById = new Map(result.projects.map((item) => [item.id, item]));
      return Object.fromEntries(result.buildings.map((item) => {
        const selected = current[item.buildingId];
        if (!applicableBuildingIds.has(item.buildingId)) {
          return [item.buildingId, selected ?? item.mapping?.projectId ?? ""];
        }
        const persisted = item.mapping?.projectId ?? "";
        if (item.buildingId === buildingId) return [item.buildingId, persisted];
        const candidate = projectById.get(selected);
        const occupiedByAnother = Boolean(
          candidate?.mappedBuildingId
          && candidate.mappedBuildingId !== item.buildingId
        );
        return [item.buildingId, selected !== undefined && !occupiedByAnother
          ? selected
          : persisted];
      }));
    });
    setFeedback((current) => {
      const next = { ...current };
      applicableBuildingIds.forEach((id) => { delete next[id]; });
      return next;
    });
  };

  const refreshAuthoritativeState = async ({ buildingId, projectIds }) => {
    const tracker = refreshVersionTrackerRef.current;
    const ticket = tracker.begin({ buildingId, projectIds });
    setRequest(buildingId, "refresh");
    let result;
    try {
      result = await fetchPrevisionProjects(companyId);
    } catch (error) {
      if (!mountedRef.current || !tracker.isCurrent(ticket)) {
        return {
          applied: false,
          superseded: true,
          coveredByNewer: tracker.coveredByNewer(ticket),
        };
      }
      throw error;
    }
    if (!mountedRef.current || !tracker.isCurrent(ticket)) {
      return {
        applied: false,
        superseded: true,
        coveredByNewer: tracker.coveredByNewer(ticket),
      };
    }
    if (
      !result
      || !Array.isArray(result.buildings)
      || !Array.isArray(result.projects)
      || !result.buildings.some((item) => item.buildingId === buildingId)
    ) {
      throw new Error("invalid authoritative Prevision response");
    }
    const applicableScopes = result.buildings
      .map((item) => ({
        buildingId: item.buildingId,
        projectIds: [item.mapping?.projectId].filter(Boolean),
      }))
      .filter((scope) => tracker.canApply(ticket, scope));
    if (!applicableScopes.some((scope) => scope.buildingId === buildingId)) {
      return {
        applied: false,
        superseded: true,
        coveredByNewer: tracker.coveredByNewer(ticket),
      };
    }
    tracker.accept(ticket, applicableScopes);
    applyAuthoritativeData(result, applicableScopes, buildingId);
    return { applied: true, superseded: false, coveredByNewer: false };
  };

  const reserveProject = (projectId, buildingId) => {
    if (projectReservationsRef.current[projectId]) return false;
    const next = { ...projectReservationsRef.current, [projectId]: buildingId };
    projectReservationsRef.current = next;
    if (mountedRef.current) setProjectReservations(next);
    return true;
  };

  const releaseProject = (projectId, buildingId) => {
    if (projectReservationsRef.current[projectId] !== buildingId) return;
    const next = { ...projectReservationsRef.current };
    delete next[projectId];
    projectReservationsRef.current = next;
    if (mountedRef.current) setProjectReservations(next);
  };

  const projectUnavailableForBuilding = (candidate, building) => {
    if (!candidate) return true;
    const currentOwnProject = candidate.mappedBuildingId === building.buildingId
      || building.mapping?.projectId === candidate.id;
    const mappedByAnother = Boolean(
      candidate.mappedBuildingId
      && candidate.mappedBuildingId !== building.buildingId
    );
    const reservedByAnother = Boolean(
      projectReservations[candidate.id]
      && projectReservations[candidate.id] !== building.buildingId
    );
    return reservedByAnother || (!currentOwnProject && (candidate.disabled || mappedByAnother));
  };

  const handleSave = async (building) => {
    if (!financialScheduleEnabled || !buildingIsEligible(building)) return;
    const projectId = selectedProjects[building.buildingId];
    const candidate = data.projects.find((item) => item.id === projectId);
    const reservationOwner = projectReservationsRef.current[projectId];
    if (
      !candidate
      || projectUnavailableForBuilding(candidate, building)
      || (reservationOwner && reservationOwner !== building.buildingId)
      || !reserveProject(projectId, building.buildingId)
    ) return;
    setRequest(building.buildingId, "save");
    setBuildingFeedback(building.buildingId, null);
    try {
      const result = await savePrevisionMapping(companyId, building.buildingId, projectId);
      try {
        const refresh = await refreshAuthoritativeState({
          buildingId: building.buildingId,
          projectIds: [projectId, building.mapping?.projectId].filter(Boolean),
        });
        if (!refresh.applied && !refresh.coveredByNewer) {
          throw new Error("authoritative refresh superseded");
        }
        setBuildingFeedback(building.buildingId, refresh.applied
          ? { kind: "success", title: "Vínculo salvo", error: null }
          : {
            kind: "neutral",
            title: "Estado confirmado por uma atualização mais recente",
            error: null,
          });
      } catch {
        applyUnconfirmedMapping(building, result.mapping);
        setSelectedProjects((current) => ({
          ...current,
          [building.buildingId]: result.mapping.projectId,
        }));
        setBuildingFeedback(building.buildingId, {
          kind: "error",
          error: {
            message: "Vínculo salvo, mas não foi possível confirmar o estado completo. Recarregue a configuração.",
            correlationId: null,
          },
        });
      }
    } catch (error) {
      setBuildingFeedback(building.buildingId, {
        kind: "error",
        error: safeError(error),
      });
    } finally {
      setRequest(building.buildingId, null);
      releaseProject(projectId, building.buildingId);
    }
  };

  const handleSync = async (building) => {
    if (!financialScheduleEnabled || !buildingIsEligible(building)) return;
    setRequest(building.buildingId, "sync");
    setBuildingFeedback(building.buildingId, null);
    try {
      await syncPrevisionBaseline(companyId, building.buildingId);
      try {
        const refresh = await refreshAuthoritativeState({
          buildingId: building.buildingId,
          projectIds: [building.mapping?.projectId].filter(Boolean),
        });
        if (!refresh.applied && !refresh.coveredByNewer) {
          throw new Error("authoritative refresh superseded");
        }
      } catch {
        updateBuilding(building.buildingId, { sync: null, scheduleSync: null });
        setBuildingFeedback(building.buildingId, {
          kind: "error",
          error: {
            message: "A sincronização terminou, mas não foi possível confirmar o estado completo. Recarregue a configuração.",
            correlationId: null,
          },
        });
      }
    } catch (error) {
      const normalized = safeError(error);
      setBuildingFeedback(building.buildingId, {
        kind: error?.code === "sync_in_progress" ? "neutral" : "error",
        error: {
          ...normalized,
          message: error?.code === "sync_in_progress"
            ? "Já existe uma sincronização em andamento"
            : normalized.message,
        },
      });
    } finally {
      setRequest(building.buildingId, null);
    }
  };

  const titleId = `prevision-projects-title-${companyId}`;

  return (
    <>
      <EscapeHandler func={onClose} />
      <Modal close={onClose}>
        <section
          className="prevision-projects-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <header className="prevision-projects-modal__header">
            <h2 id={titleId}>Configurar obras Prevision — {companyName}</h2>
            <button
              type="button"
              className="prevision-projects-modal__close"
              aria-label="Fechar configuração de obras Prevision"
              onClick={onClose}
            >
              ×
            </button>
          </header>
          <p className="prevision-projects-modal__intro">
            Selecione manualmente um projeto Prevision para cada obra. Nenhum vínculo é sugerido automaticamente.
          </p>

          {!data && !loadError ? <p>Carregando obras, projetos e estados…</p> : null}
          <ErrorMessage error={loadError} />

          {data ? (
            <>
              {!data.token.configured ? (
                <p className="prevision-token-warning">
                  Configure o token Prevision antes de vincular as obras.
                </p>
              ) : null}
              {data.token.configured && !financialScheduleEnabled ? (
                <p className="prevision-token-warning" role="alert">
                  A credencial atual acessa apenas a API REST do Prevision. Cadastre um token GraphQL para consultar o CFF financeiro, identificar a baseline ativa e sincronizar as curvas.
                </p>
              ) : null}
              {data.buildings.length === 0 ? <p>Nenhuma obra elegível para o Prevision.</p> : null}
              <div className="prevision-buildings">
                {data.buildings.map((item) => {
                  const selectedProject = selectedProjects[item.buildingId] ?? "";
                  const busy = Boolean(requests[item.buildingId]);
                  const saving = requests[item.buildingId] === "save";
                  const syncing = requests[item.buildingId] === "sync";
                  const refreshing = requests[item.buildingId] === "refresh";
                  const unchanged = selectedProject === (item.mapping?.projectId ?? "");
                  const selectedProjectData = data.projects.find((candidate) => (
                    candidate.id === selectedProject
                  ));
                  const occupiedByAnother = projectUnavailableForBuilding(selectedProjectData, item);
                  const itemFeedback = feedback[item.buildingId];

                  if (!buildingIsEligible(item)) {
                    return (
                      <article className="prevision-building" key={item.buildingId}>
                        <div className="prevision-building__identity">
                          <h3>{item.buildingName}</h3>
                          <span>WBS indisponível</span>
                        </div>
                        <div className="prevision-building__eligibility" role="status">
                          <b>Integração bloqueada</b>
                          <p>{buildingEligibilityMessage(item)}</p>
                        </div>
                      </article>
                    );
                  }

                  return (
                    <article className="prevision-building" key={item.buildingId}>
                      <div className="prevision-building__identity">
                        <h3>{item.buildingName}</h3>
                        <span>WBS <code title={item.buildingWbs}>{item.buildingWbs}</code></span>
                      </div>
                      <div className="prevision-building__project">
                        <label htmlFor={`prevision-project-${item.buildingId}`}>Projeto Prevision</label>
                        <select
                          id={`prevision-project-${item.buildingId}`}
                          aria-label={`Projeto Prevision para ${item.buildingName}`}
                          value={selectedProject}
                          disabled={!data.token.configured || !financialScheduleEnabled || busy}
                          onChange={(event) => {
                            const { value } = event.target;
                            setSelectedProjects((current) => ({
                              ...current,
                              [item.buildingId]: value,
                            }));
                            setBuildingFeedback(item.buildingId, null);
                          }}
                        >
                          <option value="">Selecione um projeto</option>
                          {data.projects.map((candidate) => {
                            const unavailable = projectUnavailableForBuilding(candidate, item);
                            const reservationOwner = projectReservations[candidate.id];
                            const reservationBuilding = reservationOwner
                              ? data.buildings.find((building) => building.buildingId === reservationOwner)
                              : null;
                            const disabledReason = reservationOwner && reservationOwner !== item.buildingId
                              ? `Vínculo sendo salvo pela obra ${reservationBuilding?.buildingName ?? reservationOwner}.`
                              : candidate.disabledReason;
                            const optionLabel = unavailable && disabledReason
                              ? `${candidate.name} — ${disabledReason}`
                              : candidate.name;
                            return (
                              <option
                                key={candidate.id}
                                value={candidate.id}
                                disabled={unavailable}
                              >
                                {optionLabel}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div className="prevision-building__actions">
                        <button
                          type="button"
                          aria-label={`Salvar vínculo da ${item.buildingName}`}
                          disabled={
                            !data.token.configured
                            || !financialScheduleEnabled
                            || !selectedProject
                            || unchanged
                            || occupiedByAnother
                            || busy
                          }
                          onClick={() => handleSave(item)}
                        >
                          {saving
                            ? "Salvando e sincronizando…"
                            : refreshing
                              ? "Atualizando estado…"
                              : "Salvar"}
                        </button>
                        {item.mapping ? (
                          <button
                            type="button"
                            className="prevision-sync-button"
                            aria-label={`Sincronizar agora a ${item.buildingName}`}
                            disabled={!financialScheduleEnabled || busy}
                            onClick={() => handleSync(item)}
                          >
                            {syncing
                              ? "Sincronizando…"
                              : refreshing
                                ? "Atualizando estado…"
                                : "Sincronizar agora"}
                          </button>
                        ) : null}
                      </div>
                      <div className="prevision-building__trail">
                        <StatusTrail sync={item.sync} />
                        <ScheduleStatusTrail sync={item.scheduleSync} loading={refreshing} />
                        {itemFeedback ? (
                          <div className={`prevision-feedback prevision-feedback--${itemFeedback.kind}`}>
                            {itemFeedback.title ? <b>{itemFeedback.title}</b> : null}
                            <ErrorMessage error={itemFeedback.error} />
                          </div>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            </>
          ) : null}
        </section>
      </Modal>
    </>
  );
};

PrevisionProjectsModal.propTypes = {
  companyId: PropTypes.string.isRequired,
  companyName: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PrevisionProjectsModal;
